import {
  forwardRef,
  ReactNode,
  MutableRefObject,
  ReactElement,
  Children,
  isValidElement,
  useEffect,
  useMemo,
} from 'react';
import { FloatingList } from '@floating-ui/react';
import { onlyText } from 'react-children-utilities';
import { useThemeValues } from '../../../../hooks/use-theme-values';
import { TextLink } from '../../../text-link';
import { useFormField } from '../../hooks';
import { SearchField } from '../search-field';

export const Menu = forwardRef<
  HTMLDivElement,
  {
    floatingProps: Record<string, unknown>;
    children: ReactNode;
    refs: {
      listItemRefs: MutableRefObject<(HTMLLIElement | null)[]>;
      labelsRef: MutableRefObject<(string | null)[]>;
      valuesRef: MutableRefObject<(string | null)[]>;
    };
    clearSelection: () => void;
    selectAll: (filter: (child: ReactElement) => boolean) => void;
    clearSelectionLabel?: string;
    selectAllLabel?: string;
    trackingIdBase?: string;
    showSearchBar?: boolean;
    showSelectionBar?: boolean;
    showSelectAll?: boolean;
  }
>(
  (
    {
      floatingProps,
      children,
      refs,
      clearSelection,
      selectAll,
      clearSelectionLabel = 'Clear',
      selectAllLabel = 'Select All',
      trackingIdBase,
      showSearchBar = true,
      showSelectionBar = true,
      showSelectAll = true,
    },
    ref
  ) => {
    const theme = useThemeValues();
    const field = useFormField({ type: 'text' });

    // Create values array synchronized with children structure, handling nested Groups
    const flattenedChildren = useMemo(() => {
      return Children.toArray(children).flatMap((child) => {
        if (
          isValidElement(child) &&
          'label' in child.props &&
          'children' in child.props &&
          typeof child.props.label === 'string'
        ) {
          // This is likely a Group component
          return Children.toArray(child.props.children);
        }
        return child;
      });
    }, [children]);

    const filtered = flattenedChildren.filter((child) =>
      onlyText(child).toLowerCase().includes(field.value.toLowerCase())
    );

    useEffect(() => {
      const valuesRef = refs.valuesRef;
      valuesRef.current = filtered.map((child) => {
        if (isValidElement(child) && child.props) {
          return (child.props as any).value || null;
        }
        return null;
      });
    }, [filtered, refs.valuesRef]);

    return (
      <div
        style={{
          outline: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(1),
        }}
        ref={ref}
        {...floatingProps}
      >
        {showSearchBar && (
          <div style={{ padding: theme.spacing(2, 2, 0) }}>
            <SearchField
              {...field}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === 'Tab') {
                  return;
                }
                e.stopPropagation();
              }}
              name='search'
            />
          </div>
        )}
        {showSelectionBar && (
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: theme.spacing(0, 2) }}>
            {showSelectAll ? (
              <TextLink
                weight='bold'
                onKeyDown={(e) => {
                  if (e.key === ' ') {
                    e.preventDefault();
                  }
                }}
                onClick={() => {
                  selectAll(
                    (child) => field.value === '' || onlyText(child).toLowerCase().includes(field.value.toLowerCase())
                  );
                }}
                trackingId={`${trackingIdBase}-select-all`}
              >
                {selectAllLabel}
              </TextLink>
            ) : (
              <div></div>
            )}
            <TextLink
              weight='bold'
              onKeyDown={(e) => {
                if (e.key === ' ') {
                  e.preventDefault();
                }
              }}
              onClick={() => {
                clearSelection();
              }}
              trackingId={`${trackingIdBase}-clear-all`}
            >
              {clearSelectionLabel}
            </TextLink>
          </div>
        )}
        <div
          style={{
            overflow: 'auto',
          }}
        >
          <FloatingList elementsRef={refs.listItemRefs} labelsRef={refs.labelsRef}>
            {/* TODO: keep grouping when filtering as well */}
            {field.value ? filtered : children}
          </FloatingList>
        </div>
      </div>
    );
  }
);

Menu.displayName = 'Menu';

export const SingleSelectionMenu = forwardRef<
  HTMLDivElement,
  {
    floatingProps: Record<string, unknown>;
    children: ReactNode;
    refs: {
      listItemRefs: MutableRefObject<(HTMLLIElement | null)[]>;
      labelsRef: MutableRefObject<(string | null)[]>;
      valuesRef: MutableRefObject<(string | null)[]>;
    };
    clearSelection: () => void;
    clearSelectionLabel?: string;
    trackingIdBase?: string;
    showSearchBar?: boolean;
    showSelectionBar?: boolean;
  }
>(
  (
    {
      floatingProps,
      children,
      refs,
      clearSelection,
      clearSelectionLabel = 'Clear',
      trackingIdBase,
      showSearchBar = true,
      showSelectionBar = true,
    },
    ref
  ) => {
    const theme = useThemeValues();
    const field = useFormField({ type: 'text' });

    // Create values array synchronized with children structure, handling nested Groups
    const flattenedChildren = useMemo(() => {
      return Children.toArray(children).flatMap((child) => {
        if (
          isValidElement(child) &&
          'label' in child.props &&
          'children' in child.props &&
          typeof child.props.label === 'string'
        ) {
          // This is likely a Group component
          return Children.toArray(child.props.children);
        }
        return child;
      });
    }, [children]);

    const filtered = flattenedChildren.filter((child) =>
      onlyText(child).toLowerCase().includes(field.value.toLowerCase())
    );

    useEffect(() => {
      const valuesRef = refs.valuesRef;
      valuesRef.current = flattenedChildren.map((child) => {
        if (isValidElement(child) && child.props) {
          return (child.props as any).value || null;
        }
        return null;
      });
    }, [filtered, flattenedChildren, refs.valuesRef]);

    return (
      <div
        style={{
          outline: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(1),
        }}
        ref={ref}
        {...floatingProps}
      >
        {showSearchBar && (
          <div style={{ padding: theme.spacing(2, 2, 0) }}>
            <SearchField
              {...field}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === 'Tab') {
                  return;
                }
                e.stopPropagation();
              }}
              name='search'
            />
          </div>
        )}
        {showSelectionBar && (
          <div
            style={{
              display: 'grid',
              justifyContent: 'end',
              padding: showSearchBar ? theme.spacing(0, 2) : theme.spacing(2, 2, 0),
            }}
          >
            <TextLink
              weight='bold'
              onKeyDown={(e) => {
                if (e.key === ' ') {
                  e.preventDefault();
                }
              }}
              onClick={() => {
                clearSelection();
              }}
              css={{
                width: 'fit-content',
              }}
              trackingId={`${trackingIdBase}-clear-all`}
            >
              {clearSelectionLabel}
            </TextLink>
          </div>
        )}
        <div
          style={{
            overflow: 'auto',
          }}
        >
          <FloatingList elementsRef={refs.listItemRefs} labelsRef={refs.labelsRef}>
            {/* TODO: keep grouping when filtering as well */}
            {field.value ? filtered : children}
          </FloatingList>
        </div>
      </div>
    );
  }
);

SingleSelectionMenu.displayName = 'SingleSelectionMenu';
